import { useLayoutEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import {
  ArrowLeft,
  BadgeCheck,
  Loader2,
  Paperclip,
  Send,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { Textarea } from '@/shared/components/ui/textarea'
import { routes } from '@/shared/constants'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { TicketStatusChip } from './components/TicketStatusChip'
import { TicketTimeline } from './components/TicketTimeline'
import { useOrgTicket } from './hooks/useOrgTicket'
import { useOrgTicketAttachments } from './hooks/useOrgTicketAttachments'
import { useOrgTicketTimeline } from './hooks/useOrgTicketTimeline'
import {
  useCommentOrgTicket,
  useConfirmOrgTicketFixed,
} from './hooks/useOrgTicketMutations'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Detalle del ticket: una LÍNEA DE TIEMPO vertical con la apertura, los
 * comentarios públicos (los internos jamás llegan — proyección server-side) y
 * los cambios de estado mezclados en orden, comentar, confirmar la corrección
 * (con confirmación explícita del usuario, decisión 4) y adjuntos
 * (presign→PUT→confirm, bucket privado).
 */
export function TicketDetailPage() {
  const { code = '' } = useParams()
  const { t } = useTranslation()
  const { user } = useAuth()
  const { detail, loading, error, refetch } = useOrgTicket(code)
  const timeline = useOrgTicketTimeline(code)
  const { comment, commenting } = useCommentOrgTicket(code)
  const { confirmFixed, confirming } = useConfirmOrgTicketFixed(code)
  const { uploadAttachment, openAttachment } = useOrgTicketAttachments()

  const [text, setText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  // Altura del documento ANTES de anteponer una página vieja: sin compensar la
  // diferencia el navegador conserva el `scrollTop` y el hilo pega un salto.
  const heightBeforePrepend = useRef<number | null>(null)
  // El comentario recién publicado se lleva el foco (y con él, el lector de
  // pantalla) al final del riel.
  const wantsTailFocus = useRef(false)

  const timelineItems = timeline.items
  useLayoutEffect(() => {
    const before = heightBeforePrepend.current
    if (before !== null) {
      heightBeforePrepend.current = null
      const delta = document.documentElement.scrollHeight - before
      // `behavior: 'instant'` a propósito: `globals.css` pone
      // `html { scroll-behavior: smooth }`, y compensar con una ANIMACIÓN se ve
      // como el salto que esto viene a evitar. La corrección tiene que ser
      // invisible, en el mismo frame en que crece el contenido.
      if (delta > 0) window.scrollBy({ top: delta, behavior: 'instant' })
    }
    if (wantsTailFocus.current) {
      wantsTailFocus.current = false
      const tail = listRef.current?.lastElementChild
      if (tail instanceof HTMLElement) {
        tail.scrollIntoView({ block: 'nearest' })
        tail.focus()
      }
    }
  }, [timelineItems])

  if (error && !detail) {
    return (
      <QueryErrorScreen
        error={error}
        onRetry={() => refetch()}
        title={t('common.error')}
      />
    )
  }
  if (loading && !detail) {
    return (
      <p className="py-12 text-center text-sm text-ink-muted">
        {t('common.loading')}
      </p>
    )
  }
  if (!detail) return null

  const { ticket } = detail

  const handleComment = async () => {
    if (!text.trim()) return
    try {
      await comment(text.trim())
      setText('')
      // La cronología vuelve a la página 1: el mensaje nuevo es el final del
      // riel, y mantener páginas viejas con offsets ya corridos abre huecos.
      wantsTailFocus.current = true
      await timeline.resetToLatest()
    } catch (e) {
      wantsTailFocus.current = false
      toast.error(e instanceof Error ? e.message : t('common.error'))
    }
  }

  const handleConfirmFixed = async () => {
    try {
      await confirmFixed()
      setConfirmOpen(false)
      toast.success(t('tickets.detail.fixConfirmed'))
      // Confirmar inserta un comentario público automático server-side: sin
      // refrescar la cronología, el cliente no lo vería aparecer.
      await timeline.resetToLatest()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'))
    }
  }

  const handleLoadOlder = async () => {
    heightBeforePrepend.current = document.documentElement.scrollHeight
    try {
      await timeline.loadOlder()
    } catch (e) {
      heightBeforePrepend.current = null
      toast.error(e instanceof Error ? e.message : t('common.error'))
    }
  }

  const handleUpload = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      await uploadAttachment(code, file)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'))
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const composer = (
    <div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('tickets.detail.commentPlaceholder')}
        rows={3}
      />
      <div className="mt-2 flex justify-end">
        <Button onClick={handleComment} disabled={commenting || !text.trim()}>
          {commenting ? (
            <Loader2 className="mr-1.5 size-4 animate-spin" />
          ) : (
            <Send className="mr-1.5 size-4" />
          )}
          {t('tickets.detail.comment')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <Link
          to={routes.app.tickets.list}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" />
          {t('tickets.title')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs text-ink-muted">{ticket.code}</p>
            <h1 className="font-serif text-[30px] font-normal leading-[1.15] tracking-tight text-ink">
              {ticket.title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
              <TicketStatusChip status={ticket.status} />
              <span className="capitalize">{ticket.type}</span>
              {ticket.businessName && <span>· {ticket.businessName}</span>}
              <span>· {format(new Date(ticket.createdAt), 'dd/MM/yyyy')}</span>
              {detail.fixConfirmedAt && (
                <Badge variant="active">
                  <BadgeCheck className="mr-1 size-3" />
                  {t('tickets.detail.fixConfirmed')}
                </Badge>
              )}
            </div>
          </div>
          {!detail.fixConfirmedAt && (
            <Button onClick={() => setConfirmOpen(true)} disabled={confirming}>
              <BadgeCheck className="mr-1.5 size-4" />
              {t('tickets.detail.confirmFixed')}
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            {t('tickets.detail.timelineTitle')}
          </h2>

          {timeline.error && timelineItems.length === 0 ? (
            <QueryErrorScreen
              error={timeline.error}
              onRetry={() => timeline.refetch()}
              title={t('tickets.detail.timelineError')}
            />
          ) : timeline.initialLoading ? (
            <p className="py-12 text-center text-sm text-ink-muted">
              {t('common.loading')}
            </p>
          ) : (
            <TicketTimeline
              ticketCreatedAt={ticket.createdAt}
              description={detail.description}
              items={timelineItems}
              currentUserId={user?.id}
              hasMore={timeline.hasMore}
              loadingOlder={timeline.loadingOlder}
              onLoadOlder={handleLoadOlder}
              composer={composer}
              listRef={listRef}
            />
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
              {t('tickets.detail.attachments')}
            </h2>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              onChange={(e) => void handleUpload(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? (
                <Loader2 className="mr-1.5 size-4 animate-spin" />
              ) : (
                <Upload className="mr-1.5 size-4" />
              )}
              {t('tickets.form.attachments')}
            </Button>
          </div>
          {detail.attachments.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-muted">—</p>
          ) : (
            <ul className="space-y-2">
              {detail.attachments.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => void openAttachment(code, a.id)}
                    className="flex w-full items-center gap-2 rounded-lg border border-black/5 p-2.5 text-left text-sm hover:bg-neutral-50"
                  >
                    <Paperclip className="size-4 shrink-0 text-ink-muted" />
                    <span className="min-w-0 flex-1 truncate">{a.name}</span>
                    <span className="shrink-0 text-xs text-ink-muted">
                      {formatBytes(a.sizeBytes)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Confirmación EXPLÍCITA del usuario antes de confirmar la corrección. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tickets.detail.confirmFixed')}</DialogTitle>
            <DialogDescription>
              {t('tickets.detail.confirmFixedQuestion')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirmFixed} disabled={confirming}>
              {confirming && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {t('tickets.detail.confirmFixed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
